import { take } from 'rxjs';

import type {
  OwnTransportIngredient,
  RecipeMainIngredient,
  StayIngredient,
} from '@apps/main/modules/recipe/ingredients';
import {
  isFerryIngredient,
  isFlightIngredient,
  isHotelIngredient,
  isOwnTransportIngredient,
  isStayIngredient,
  isBookableTransportIngredient,
} from '@apps/main/modules/recipe/ingredients';
import { FlightEndpoint } from '@apps/main/modules/api/flight';
import type { EditTripSegment } from '@apps/main/new-desktop/modules/editTripModal';
import type { CarTripInfo } from '@apps/main/new-desktop/pages/carTrip/models';

import type { RouteConnectionWithDetails } from '@libs/maps';
import { getHumanizedDuration } from '@libs/utils';
import type {
  AirportStaticInfo,
  Coordinate,
  IngredientId,
} from '@libs/trip-planner-interfaces';

export function getNewRecipeOrder(
  segments: EditTripSegment[],
  recipe: RecipeMainIngredient[]
): RecipeMainIngredient[] {
  const stayOnlyRecipe = recipe.filter((ingredient) =>
    isStayIngredient(ingredient)
  );

  interface ChangedStays {
    stayId: IngredientId;
    oldIndex: number;
  }
  const changedStays: ChangedStays[] = [];

  const recipeStayIds = stayOnlyRecipe.map(
    (ingredient) => ingredient.ingredientId
  );
  const segmentsStayIds = segments.map((segment) => segment.ingredientId);

  segmentsStayIds.forEach((id, newIndex) => {
    const oldIndex = recipeStayIds.indexOf(id);
    if (oldIndex !== newIndex) {
      changedStays.push({ stayId: id, oldIndex });
    }
  });

  if (changedStays.length === 0) {
    return recipe;
  }

  const allPreviousTransportIsOwnTransport = changedStays.every(
    (changedStay) => {
      const stayIndexInRecipe = recipe.findIndex(
        (ingredient) => ingredient.ingredientId === changedStay.stayId
      );
      const previousTransport = recipe[stayIndexInRecipe - 1];

      return previousTransport && isOwnTransportIngredient(previousTransport);
    }
  );

  const result: RecipeMainIngredient[] = [];
  const addedIngredientIds = new Set<string>();

  function findById(id: string): RecipeMainIngredient | undefined {
    return recipe.find((ingredient) => ingredient.ingredientId === id);
  }

  function findPreviousOwnTransport(
    id: string
  ): OwnTransportIngredient | undefined {
    const index = recipe.findIndex((ing) => ing.ingredientId === id);
    const previousIngredient = recipe[index - 1];
    return previousIngredient && isOwnTransportIngredient(previousIngredient)
      ? previousIngredient
      : undefined;
  }

  segments.forEach(({ ingredientId }) => {
    const hotel = findById(ingredientId);
    if (!hotel || !isStayIngredient(hotel)) {
      return;
    }
    const previousOwnTransport = findPreviousOwnTransport(hotel.ingredientId);
    if (
      allPreviousTransportIsOwnTransport &&
      previousOwnTransport &&
      !addedIngredientIds.has(previousOwnTransport.ingredientId)
    ) {
      result.push(previousOwnTransport);
      addedIngredientIds.add(previousOwnTransport.ingredientId);
    }
    result.push(hotel);
    addedIngredientIds.add(hotel.ingredientId);
  });
  recipe.forEach((ingredient, index) => {
    if (addedIngredientIds.has(ingredient.ingredientId)) {
      return;
    }
    if (
      isBookableTransportIngredient(ingredient) ||
      isOwnTransportIngredient(ingredient)
    ) {
      result.splice(index, 0, ingredient);
      addedIngredientIds.add(ingredient.ingredientId);
    }
  });
  return result;
}

export function getEditTripSegmentFromStayIngredient(
  ingredient: StayIngredient,
  calculatedRoute?: RouteConnectionWithDetails
): EditTripSegment {
  let carTripInfo: CarTripInfo | undefined;
  if (
    calculatedRoute?.distanceInKilometers &&
    calculatedRoute.expectedTravelTimeInMinutes
  ) {
    carTripInfo = {
      distance: `${calculatedRoute.distanceInKilometers.toFixed()}km`,
      duration: getHumanizedDuration(
        calculatedRoute.expectedTravelTimeInMinutes,
        'minute'
      ),
    };
  }
  return {
    destinationName: ingredient.displayText,
    numberOfNights: ingredient.numberOfNights,
    ingredientId: ingredient.ingredientId,
    carTripInfo,
  };
}

export function updateRecipeTransportPorts(
  recipe: RecipeMainIngredient[],
  flightEndpoint: FlightEndpoint = new FlightEndpoint()
): RecipeMainIngredient[] {
  return recipe.reduce(
    (
      acc: RecipeMainIngredient[],
      ingredient: RecipeMainIngredient,
      index: number
    ): RecipeMainIngredient[] => {
      let findByPrevious: string | Coordinate | undefined;
      let findByNext: string | Coordinate | undefined;

      const previousIngredient = recipe[index - 1];
      if (previousIngredient && isHotelIngredient(previousIngredient)) {
        findByPrevious = previousIngredient.getSearchText();
      }

      const nextIngredient = recipe[index + 1];
      if (nextIngredient && isHotelIngredient(nextIngredient)) {
        findByNext = nextIngredient.getSearchText();
      }

      if (findByPrevious && isFlightIngredient(ingredient)) {
        flightEndpoint
          .getSuggestedAirport(findByPrevious)
          .pipe(take(1))
          .subscribe((airportStaticInfo: AirportStaticInfo | undefined) => {
            if (
              airportStaticInfo &&
              airportStaticInfo.code !== ingredient.fromPort
            ) {
              ingredient.setDepartureAirport({
                iata: airportStaticInfo.code,
                name: airportStaticInfo.city,
                displayingText: airportStaticInfo.label,
              });
            }
          });
      }

      if (findByNext && isFlightIngredient(ingredient)) {
        flightEndpoint
          .getSuggestedAirport(findByNext)
          .pipe(take(1))
          .subscribe((airportStaticInfo: AirportStaticInfo | undefined) => {
            if (
              airportStaticInfo &&
              airportStaticInfo.code !== ingredient.toPort
            ) {
              ingredient.setArrivalAirport({
                iata: airportStaticInfo.code,
                name: airportStaticInfo.city,
                displayingText: airportStaticInfo.label,
              });
            }
          });
      }

      if (isFerryIngredient(ingredient)) {
        // NOTE: if it is ferry ingredient we want to remove it from the recipe as with current API its hard to get suggestions to random destinations
        return acc;
      }
      acc.push(ingredient);
      return acc;
    },
    []
  );
}
